// src/components/EBusinessCard.js
import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode.react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/EBusinessCard.css';
import { API_BASE } from "../../config/apiBase";

const EBusinessCard = () => {
  const { token, userId } = useAuth();
  const [userDetails, setUserDetails] = useState({});
  const [isFlipped, setIsFlipped] = useState(false);
  const [selectedDesign, setSelectedDesign] = useState('businessCardBackground');
  const saveTimeout = useRef(null);

  const API = API_BASE;

  useEffect(() => {
    if (userId) {
      fetch(`${API}/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch user details');
          }
          return response.json();
        })
        .then(data => setUserDetails(data))
        .catch(error => {
          console.error(error);
          toast.error('Error fetching user details', { position: 'bottom-left' });
        });
    }
  }, [token, userId]);

  const debounceSaveChanges = (newDetails) => {
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }
    saveTimeout.current = setTimeout(() => saveChanges(newDetails), 1000);
  };

  const saveChanges = async (newDetails) => {
    try {
      const response = await fetch(`${API}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newDetails),
      });

      if (!response.ok) {
        throw new Error('Failed to update user details');
      }
      toast.success('User updated successfully', { position: 'bottom-left' });
    } catch (error) {
      console.error(error);
      toast.error('Error updating user details', { position: 'bottom-left' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newDetails = { ...userDetails, [name]: value };
    setUserDetails(newDetails);
    debounceSaveChanges(newDetails);
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const generateVCard = () => {
    return `BEGIN:VCARD
      VERSION:3.0
      FN:${userDetails.userFirstName || ''}
      N:${userDetails.userLastName || ''};${userDetails.userFirstName || ''}
      TITLE:${userDetails.jobTitle || ''}
      ORG:${userDetails.companyName || ''}
      TEL;TYPE=WORK,VOICE:${userDetails.phone || ''}
      EMAIL;TYPE=PREF,INTERNET:${userDetails.email || ''}
      ADR;TYPE=WORK:;;${userDetails.address || ''};${userDetails.city || ''};;;
      URL:${userDetails.instagram || ''}
      URL:${userDetails.linkedin || ''}
      URL:${userDetails.facebook || ''}
      URL:${userDetails.twitter || ''}
      END:VCARD`;
  };

  const handleDesignChange = (e) => {
    setSelectedDesign(e.target.value);
  };

  return (
    <div className="business-card-container flex-sm-column-cc">
      <div className={`business-card business-card-${selectedDesign}`}>
        <div className={`business-card-inner ${isFlipped ? 'flipped' : ''}`}>
          <div className="business-card-front">
            <button className="swap-button" onClick={handleFlip}>
              <i className="bi bi-arrow-left-right fs-5 m-1"></i>
            </button>
            <div className="business-card-content">
              <div className="business-card-left mx-2">
                <h2>{`${userDetails.userFirstName} ${userDetails.userLastName}`}</h2>
                <h4>{userDetails.jobTitle}</h4>
                <div className='mt-3'>
                  <p className="business-card-descr">{userDetails.companyName}</p>
                  <p className="business-card-descr">{userDetails.address}</p>
                  <p className="business-card-descr">{userDetails.city}</p>
                  <p className="business-card-descr">{userDetails.email}</p>
                  <p className="business-card-descr">{userDetails.phone}</p>
                </div>
                <div className='row business-card-socials'>
                  <div className='col-md-5 col-sm-12'>
                    <span className='socials'><i className="bi bi-linkedin fs-5 ml-0 me-2"></i>{userDetails.linkedin}</span>
                  </div>
                  <div className='col-md-7 col-sm-12'>
                    <span className='socials'><i className="bi bi-instagram fs-5 ml-0 me-2"></i>{userDetails.instagram}</span>
                  </div>
                  <div className='col-md-5 col-sm-12'>
                    <span className='socials'><i className="bi bi-facebook fs-5 ml-0 me-2"></i>{userDetails.facebook}</span>
                  </div>
                  <div className='col-md-7 col-sm-12'>
                    <span className='socials fs-8'><i className="bi bi-twitter fs-5 ml-0 me-2"></i>{userDetails.twitter}</span>
                  </div>
                </div>
              </div>
              <div className="business-card-right qr-code">
                <QRCode value={generateVCard()} size={230} style={{border: "10px solid rgb(120 99 45)", margin: "5px 0px"}} />
                <div className='d-flex justify-content-center'>
                  <p>Get in touch instantly!</p>
                </div>
              </div>
            </div>
          </div>
          <div className="business-card-back">
            <button className="swap-button" onClick={handleFlip}>
              <i className="bi bi-arrow-left-right fs-5 mx-2"></i>
            </button>
            <div className="business-card-form row w-100 overflow-hidden">
              <div className="col-6 mb-1">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="userFirstName"
                    name="userFirstName"
                    value={userDetails.userFirstName || ''}
                    onChange={handleChange}
                    placeholder="First Name"
                  />
                  <label htmlFor="userFirstName">First Name</label>
                </div>
              </div>
              <div className="col-6 mb-1">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="userLastName"
                    name="userLastName"
                    value={userDetails.userLastName || ''}
                    onChange={handleChange}
                    placeholder="Last Name"
                  />
                  <label htmlFor="userLastName">Last Name</label>
                </div>
              </div>
              <div className="col-6 mb-1">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="jobTitle"
                    name="jobTitle"
                    value={userDetails.jobTitle || ''}
                    onChange={handleChange}
                    placeholder="Job Title"
                  />
                  <label htmlFor="jobTitle">Job Title</label>
                </div>
              </div>
              <div className="col-6 mb-1">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="companyName"
                    name="companyName"
                    value={userDetails.companyName || ''}
                    onChange={handleChange}
                    placeholder="Company Name"
                  />
                  <label htmlFor="companyName">Company Name</label>
                </div>
              </div>
              <div className="col-6 mb-1">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="address"
                    name="address"
                    value={userDetails.address || ''}
                    onChange={handleChange}
                    placeholder="Address"
                  />
                  <label htmlFor="address">Address</label>
                </div>
              </div>
              <div className="col-6 mb-1">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="city"
                    name="city"
                    value={userDetails.city || ''}
                    onChange={handleChange}
                    placeholder="City"
                  />
                  <label htmlFor="city">City</label>
                </div>
              </div>
              <div className="col-6 mb-1">
                <div className="form-floating">
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    value={userDetails.email || ''}
                    onChange={handleChange}
                    placeholder="Email"
                  />
                  <label htmlFor="email">Email</label>
                </div>
              </div>
              <div className="col-6 mb-1">
                <div className="form-floating">
                  <input
                    type="tel"
                    className="form-control"
                    id="phone"
                    name="phone"
                    value={userDetails.phone || ''}
                    onChange={handleChange}
                    placeholder="Phone"
                  />
                  <label htmlFor="phone">Phone</label>
                </div>
              </div>
              <div className="col-6 mb-1">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="instagram"
                    name="instagram"
                    value={userDetails.instagram || ''}
                    onChange={handleChange}
                    placeholder="Instagram"
                  />
                  <label htmlFor="instagram">Instagram</label>
                </div>
              </div>
              <div className="col-6 mb-1">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="linkedin"
                    name="linkedin"
                    value={userDetails.linkedin || ''}
                    onChange={handleChange}
                    placeholder="LinkedIn"
                  />
                  <label htmlFor="linkedin">LinkedIn</label>
                </div>
              </div>
              <div className="col-6 mb-1">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="facebook"
                    name="facebook"
                    value={userDetails.facebook || ''}
                    onChange={handleChange}
                    placeholder="Facebook"
                  />
                  <label htmlFor="facebook">Facebook</label>
                </div>
              </div>
              <div className="col-6 mb-1">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="twitter"
                    name="twitter"
                    value={userDetails.twitter || ''}
                    onChange={handleChange}
                    placeholder="Twitter"
                  />
                  <label htmlFor="twitter">Twitter</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="design-selector">
        <label>
          <input
            type="radio"
            name="design"
            value="businessCardBackground"
            checked={selectedDesign === 'businessCardBackground'}
            onChange={handleDesignChange}
          />
          <span className="design-preview design"></span>
        </label>
        <label>
          <input
            type="radio"
            name="design"
            value="businessCardBackground2"
            checked={selectedDesign === 'businessCardBackground2'}
            onChange={handleDesignChange}
          />
          <span className="design-preview design2"></span>
        </label>
        <label>
          <input
            type="radio"
            name="design"
            value="businessCardBackground3"
            checked={selectedDesign === 'businessCardBackground3'}
            onChange={handleDesignChange}
          />
          <span className="design-preview design3"></span>
        </label>
        <label>
          <input
            type="radio"
            name="design"
            value="businessCardBackground4"
            checked={selectedDesign === 'businessCardBackground4'}
            onChange={handleDesignChange}
          />
          <span className="design-preview design4"></span>
        </label>
        <label>
          <input
            type="radio"
            name="design"
            value="businessCardBackground5"
            checked={selectedDesign === 'businessCardBackground5'}
            onChange={handleDesignChange}
          />
          <span className="design-preview design5"></span>
        </label>
        <label>
          <input
            type="radio"
            name="design"
            value="businessCardBackground6"
            checked={selectedDesign === 'businessCardBackground6'}
            onChange={handleDesignChange}
          />
          <span className="design-preview design6"></span>
        </label>
      </div>
    </div>
  );
};

export default EBusinessCard;
